/**
 * Device class and install state (Phase 16A, design §2.1 / §4).
 *
 * Offline mode and the visit start / end / signature actions are for phones and tablets only.
 * The class comes from the OS, not the screen width: a touch laptop is DESKTOP, an iPad is MOBILE.
 * Pure functions take the navigator-like input so they can be tested without a browser.
 */

export type DeviceClass = 'MOBILE' | 'DESKTOP';
export type DevicePlatform = 'ios' | 'ipados' | 'android' | 'desktop';

/** The navigator fields the detection reads (a subset of Navigator). */
export interface NavigatorLike {
  userAgent: string;
  platform?: string;
  maxTouchPoints?: number;
  userAgentData?: { mobile?: boolean; platform?: string };
}

const currentNavigator = (): NavigatorLike | null =>
  typeof navigator === 'undefined' ? null : (navigator as unknown as NavigatorLike);

/** OS family of the device. iPadOS Safari reports itself as a Mac, so touch points decide. */
export function detectPlatform(nav: NavigatorLike | null = currentNavigator()): DevicePlatform {
  if (!nav) return 'desktop';
  const ua = nav.userAgent || '';
  const uaPlatform = nav.userAgentData?.platform || '';
  if (/android/i.test(ua) || /android/i.test(uaPlatform)) return 'android';
  if (/iPhone|iPod/.test(ua)) return 'ios';
  if (/iPad/.test(ua)) return 'ipados';
  if ((nav.platform === 'MacIntel' || /Macintosh/.test(ua)) && (nav.maxTouchPoints ?? 0) > 1) return 'ipados';
  return 'desktop';
}

export function detectDeviceClass(nav: NavigatorLike | null = currentNavigator()): DeviceClass {
  return detectPlatform(nav) === 'desktop' ? 'DESKTOP' : 'MOBILE';
}

/** Running as an installed app (Home Screen / launcher), not in a browser tab. */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  const displayStandalone = typeof window.matchMedia === 'function'
    && (window.matchMedia('(display-mode: standalone)').matches || window.matchMedia('(display-mode: fullscreen)').matches);
  return iosStandalone || displayStandalone;
}

/**
 * Embedded browsers (links opened from email, social or chat apps) can't install the app or keep
 * its storage. Matches the common in-app browser markers and Android WebView ("; wv)").
 */
export function isInAppBrowser(userAgent: string = currentNavigator()?.userAgent || ''): boolean {
  return /; wv\)|FBAN|FBAV|Instagram|Line\/|LinkedInApp|Snapchat|GSA\//.test(userAgent);
}

/** Firefox has no install prompt (Android: menu install only). */
export function isFirefox(userAgent: string = currentNavigator()?.userAgent || ''): boolean {
  return /Firefox\/|FxiOS\//.test(userAgent);
}

/** Display label for the device registry ("iPhone", "Android tablet", ...); no user data. */
export function deviceLabel(nav: NavigatorLike | null = currentNavigator()): string {
  const platform = detectPlatform(nav);
  if (platform === 'ios') return 'iPhone';
  if (platform === 'ipados') return 'iPad';
  if (platform === 'android') return /Mobile/.test(nav?.userAgent || '') ? 'Android phone' : 'Android tablet';
  return 'Computer';
}
